/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import instance from '../../api/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_BADGE = {
  UNDER_REVIEW:           { bg: '#f1f5f9', text: '#475569' },
  ASSESSMENT_IN_PROGRESS: { bg: '#eff6ff', text: '#2563eb' },
  APPROVED_FOR_ACTION:    { bg: '#eef2ff', text: '#4f46e5' },
  COMPLETED:              { bg: '#f0fdf4', text: '#16a34a' },
  CLOSED:                 { bg: '#f0fdf4', text: '#16a34a' },
  REJECTED:               { bg: '#fee2e2', text: '#dc2626' },
  INCOMPLETE:             { bg: '#fff7ed', text: '#ea580c' },
  MONITORING:             { bg: '#f0fdf4', text: '#15803d' },
  REASSESSMENT:           { bg: '#fdf4ff', text: '#9333ea' },
};

const LEVEL_STYLE = {
  Low:     { bg: '#f0fdf4', text: '#16a34a', brd: '#bbf7d0' },
  Medium:  { bg: '#fefce8', text: '#ca8a04', brd: '#fde68a' },
  High:    { bg: '#fff7ed', text: '#ea580c', brd: '#fdba74' },
  Extreme: { bg: '#fee2e2', text: '#dc2626', brd: '#fca5a5' },
};

const PIE_COLORS = ['#3C6E71','#F4A261','#2A5CAA','#E07B39','#264653','#6D6875','#457B9D','#A8DADC','#81B29A','#F3722C'];

const PIPELINE_ROUTE = {
  UNDER_REVIEW:            '/admin/adminriskregistry',
  ASSESSMENT_IN_PROGRESS:  '/admin/adminriskassessment',
  ASSESSMENT_COMPLETE:     '/admin/adminassessmentfinalization',
  APPROVED_FOR_ASSESSMENT: '/admin/adminriskselection',
  APPROVED_FOR_ACTION:     '/admin/adminInitialResponse',
  INITIAL_RESPONSE:        '/admin/adminInitialResponse',
  RESPONSE_REVIEW:         '/admin/adminresponsereview',
  RESPONSE_SCORING:        '/admin/adminriskresponsescoring',
  RESPONSE_FINALIZATION:   '/admin/adminriskresponsefinalization',
  MONITORING:              '/admin/adminmonitoringplan',
  REASSESSMENT:            '/admin/adminriskreassessment',
  COMPLETED:               '/admin/adminriskmanagement',
  CLOSED:                  '/admin/adminriskmanagement',
  REJECTED:                '/admin/adminriskregistry',
  INCOMPLETE:              '/admin/adminriskregistry',
};

// Heatmap colour by L×I score
function heatColor(score) {
  if (score >= 20) return '#dc2626'; // Critical/Extreme
  if (score >= 12) return '#ea580c'; // High
  if (score >= 6)  return '#f59e0b'; // Medium
  return '#22c55e';                  // Low
}

function statusLabel(s) {
  return s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) ?? s;
}

// ── Primitives ─────────────────────────────────────────────────────────────────

function Skeleton({ className }) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />;
}

function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-sm font-bold text-gray-800 mb-4 tracking-tight" style={{ letterSpacing: '-0.2px' }}>
      {children}
    </h2>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] ?? { bg: '#f5f3ff', text: '#7c3aed' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
      borderRadius: 100, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.text,
    }}>
      {statusLabel(status)}
    </span>
  );
}

function LevelBadge({ level }) {
  const s = LEVEL_STYLE[level] ?? { bg: '#f1f5f9', text: '#64748b', brd: '#e2e8f0' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.text, border: `1px solid ${s.brd}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.text, display: 'inline-block' }} />
      {level}
    </span>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

// ── KPI Card ───────────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon, accentColor, loading }) {
  if (loading) {
    return (
      <Card className="p-5">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-8 w-16 mb-2" />
      </Card>
    );
  }
  return (
    <Card className="p-5 hover:shadow-md transition-shadow">
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#94a3b8', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
          {value ?? '—'}
        </div>
        <div style={{ marginBottom: 3, color: accentColor }}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

// ── Risk Heat Map ──────────────────────────────────────────────────────────────
// Displays a 5×5 Likelihood × Impact grid.
// heatCells: array of { likelihood: 1-5, impact: 1-5, count: number }
function RiskHeatMap({ heatCells, loading }) {
  // Build lookup: key = `${likelihood}-${impact}` → count
  const cellMap = {};
  (heatCells || []).forEach(c => {
    cellMap[`${c.likelihood}-${c.impact}`] = (cellMap[`${c.likelihood}-${c.impact}`] || 0) + c.count;
  });

  if (loading) {
    return (
      <div style={{ padding: 22 }}>
        <Skeleton className="h-5 w-40 mb-4" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div style={{ padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.2px' }}>
          Risk Heat Map
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>
          Likelihood × Impact
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {/* Y-axis: Likelihood labels (5 at top → 1 at bottom) */}
        <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 20 }}>
          {[5, 4, 3, 2, 1].map(l => (
            <div key={l} style={{ height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, fontSize: 10, fontWeight: 700, color: '#94a3b8' }}>
              {l}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ flex: 1 }}>
          {/* X-axis header */}
          <div style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{i}</div>
            ))}
          </div>
          {/* Rows (likelihood 5 → 1) */}
          {[5, 4, 3, 2, 1].map(l => (
            <div key={l} style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
              {[1, 2, 3, 4, 5].map(i => {
                const score = l * i;
                const count = cellMap[`${l}-${i}`] || 0;
                const bg = heatColor(score);
                return (
                  <div
                    key={i}
                    title={`L${l} × I${i} = ${score}${count > 0 ? ` (${count} risk${count > 1 ? 's' : ''})` : ''}`}
                    style={{
                      flex: 1, height: 38, background: bg, borderRadius: 5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative', cursor: count > 0 ? 'default' : 'default',
                    }}
                  >
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontWeight: 600 }}>{score}</span>
                    {count > 0 && (
                      <div style={{
                        position: 'absolute', width: 20, height: 20, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.28)', border: '1.5px solid rgba(255,255,255,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800, color: '#fff',
                      }}>
                        {count}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Axis labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingLeft: 24 }}>
        <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>← Impact →</span>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 10, justifyContent: 'center' }}>
        {[
          { color: '#22c55e', label: 'Low' },
          { color: '#f59e0b', label: 'Medium' },
          { color: '#ea580c', label: 'High' },
          { color: '#dc2626', label: 'Critical' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#64748b' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Icon helpers ───────────────────────────────────────────────────────────────

const IconRefresh = ({ spin }) => (
  <svg className={`w-4 h-4 ${spin ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data,      setData]      = useState(null);
  const [heatData,  setHeatData]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Main overview (existing endpoint)
      const res = await instance.get('/api/admin/dashboard/overview');
      const overview = res.data?.data ?? res.data;
      setData(overview);

      // Heatmap endpoint — expected: { data: [{ likelihood, impact, count }] }
      // Falls back gracefully if endpoint doesn't exist yet
      if (overview?.heatmap) {
        setHeatData(overview.heatmap);
      } else {
        try {
          const hRes = await instance.get('/api/admin/dashboard/heatmap');
          setHeatData(hRes.data?.data ?? hRes.data ?? []);
        } catch {
          setHeatData([]);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-gray-600 text-sm max-w-sm text-center">
          Something went wrong while loading the dashboard. Please try again.
        </p>
        <button onClick={fetchData} className="px-4 py-2 bg-caribbeanCurrent text-white rounded-lg text-sm hover:opacity-90 transition font-medium">
          Retry
        </button>
      </div>
    );
  }

  const s = data?.summary ?? {};

  const inherent = data?.risksByLevel?.inherent ?? {};
  const residual = data?.risksByLevel?.residual ?? {};
  const riskLevelData = ['Low', 'Medium', 'High', 'Extreme'].map(lv => ({
    level: lv,
    Inherent: inherent[lv] ?? 0,
    Residual:  residual[lv]  ?? 0,
  }));

  const pipeline    = data?.pipeline ?? [];
  const pipelineMax = Math.max(...pipeline.map(p => p.count), 1);

  return (
    <div className="space-y-5 pb-8 fade-in">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.4px' }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>Risk management overview</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all disabled:opacity-40"
          style={{ color: '#1d4ed8', border: '1.5px solid #1d4ed8', background: 'transparent' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1d4ed8'; }}
        >
          <IconRefresh spin={loading} />
          Refresh
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard loading={loading} label="Total Risks"   value={s.totalRisks}       accentColor="#3b82f6"
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>} />
        <KpiCard loading={loading} label="Active Risks"  value={s.activeRisks}      accentColor="#ea580c"
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>} />
        <KpiCard loading={loading} label="Completed"     value={s.completedRisks}   accentColor="#16a34a"
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>} />
        <KpiCard loading={loading} label="Total Users"   value={s.totalUsers}       accentColor="#6366f1"
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>} />
        <KpiCard loading={loading} label="Departments"   value={s.totalDepartments} accentColor="#8b5cf6"
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>} />
      </div>

      {/* ── Pipeline + Heat Map (side by side) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Risk Pipeline */}
        <Card className="p-5">
          <SectionTitle>Risk Pipeline</SectionTitle>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-4 w-36 flex-shrink-0" />
                  <Skeleton className="h-6 flex-1" />
                  <Skeleton className="h-4 w-7 flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : pipeline.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No pipeline data</p>
          ) : (
            <div className="space-y-2.5">
              {pipeline.map((stage) => {
                const pct   = Math.round((stage.count / pipelineMax) * 100);
                const route = PIPELINE_ROUTE[stage.status];
                return (
                  <div
                    key={stage.status}
                    className="flex items-center gap-3 group"
                    style={{ cursor: route ? 'pointer' : 'default' }}
                    onClick={() => route && navigate(route)}
                  >
                    <span style={{ fontSize: 11, color: '#64748b', width: 160, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {statusLabel(stage.status)}
                    </span>
                    <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%', borderRadius: 4,
                          width: `${Math.max(pct, stage.count > 0 ? 2 : 0)}%`,
                          background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)',
                          transition: 'width 0.6s ease',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', width: 24, textAlign: 'right', flexShrink: 0 }}>
                      {stage.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Risk Heat Map */}
        <Card>
          <RiskHeatMap heatCells={heatData} loading={loading} />
        </Card>
      </div>

      {/* ── By Perspective (donut) + By Department (bars) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>

        {/* Donut — Risks by Perspective */}
        <Card className="p-5">
          <SectionTitle>By Perspective</SectionTitle>
          {loading ? <Skeleton className="h-56 w-full" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={data?.risksByPerspective ?? []}
                  dataKey="count"
                  nameKey="perspective"
                  cx="50%"
                  cy="45%"
                  innerRadius={46}
                  outerRadius={74}
                  paddingAngle={3}
                >
                  {(data?.risksByPerspective ?? []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, _n, entry) => [v, entry.payload.perspective]} />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  formatter={(_, entry) => entry.payload.perspective}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Horizontal bars — Risks by Department */}
        <Card className="p-5">
          <SectionTitle>Risks by Department</SectionTitle>
          {loading ? <Skeleton className="h-56 w-full" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={(data?.risksByDepartment ?? []).slice(0, 8)}
                margin={{ top: 4, right: 20, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="department" type="category" tick={{ fontSize: 10 }} width={90} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Risks">
                  {(data?.risksByDepartment ?? []).slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* ── Inherent vs Residual + Monthly Trend ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        <Card className="p-5">
          <SectionTitle>Inherent vs Residual Risk</SectionTitle>
          {loading ? <Skeleton className="h-56 w-full" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={riskLevelData} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="level" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Inherent" fill="#F4A261" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Residual"  fill="#3C6E71" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <SectionTitle>Monthly Trend</SectionTitle>
          {loading ? <Skeleton className="h-56 w-full" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data?.monthlyTrends ?? []} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="created"   stroke="#1d4ed8" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Created" />
                <Line type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Completed" />
                <Line type="monotone" dataKey="closed"    stroke="#9ca3af" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Closed" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* ── Top 10 Highest-Risk Items ── */}
      <Card className="p-5">
        <SectionTitle>Top 10 Highest-Risk Items</SectionTitle>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #f1f5f9' }}>
                  <th className="pb-3 pr-4 font-medium text-left">Risk #</th>
                  <th className="pb-3 pr-4 font-medium text-left">Risk Name</th>
                  <th className="pb-3 pr-4 font-medium text-left">Department</th>
                  <th className="pb-3 pr-4 font-medium text-right">Inh. Score</th>
                  <th className="pb-3 pr-4 font-medium text-left">Inh. Level</th>
                  <th className="pb-3 pr-4 font-medium text-right">Res. Score</th>
                  <th className="pb-3 pr-4 font-medium text-left">Res. Level</th>
                  <th className="pb-3 font-medium text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.topRisks ?? []).map((r, i) => (
                  <tr key={r.riskRegisterId ?? i} style={{ borderBottom: '1px solid #f8fafc' }}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 pr-4">
                      <span style={{
                        display: 'inline-flex', fontSize: 11, fontWeight: 800,
                        background: LEVEL_STYLE[r.inherentLevel]?.bg ?? '#f1f5f9',
                        color: LEVEL_STYLE[r.inherentLevel]?.text ?? '#64748b',
                        padding: '3px 8px 3px 6px', borderRadius: 6,
                        borderLeft: `3px solid ${LEVEL_STYLE[r.inherentLevel]?.text ?? '#94a3b8'}`,
                        whiteSpace: 'nowrap',
                      }}>
                        {r.riskNumber}
                      </span>
                    </td>
                    <td className="py-3 pr-4" style={{ maxWidth: 200 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.riskName}
                      </span>
                    </td>
                    <td className="py-3 pr-4" style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{r.department}</td>
                    <td className="py-3 pr-4 text-right" style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{r.inherentScore}</td>
                    <td className="py-3 pr-4"><LevelBadge level={r.inherentLevel} /></td>
                    <td className="py-3 pr-4 text-right" style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{r.residualScore}</td>
                    <td className="py-3 pr-4"><LevelBadge level={r.residualLevel} /></td>
                    <td className="py-3"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
                {(data?.topRisks ?? []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400 text-sm">No high-risk items found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Recent Activity ── */}
      <Card className="p-5">
        <SectionTitle>Recent Activity</SectionTitle>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #f1f5f9' }}>
                  <th className="pb-3 pr-4 font-medium text-left">Risk #</th>
                  <th className="pb-3 pr-4 font-medium text-left">Risk Name</th>
                  <th className="pb-3 pr-4 font-medium text-left">Status</th>
                  <th className="pb-3 pr-4 font-medium text-left">Department</th>
                  <th className="pb-3 pr-4 font-medium text-left">Submitted By</th>
                  <th className="pb-3 font-medium text-left">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recentRisks ?? []).map((r, i) => (
                  <tr key={r.id ?? i} style={{ borderBottom: '1px solid #f8fafc' }}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 pr-4">
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', fontFamily: 'monospace' }}>
                        {r.riskNumber}
                      </span>
                    </td>
                    <td className="py-3 pr-4" style={{ maxWidth: 200 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.riskName}
                      </span>
                    </td>
                    <td className="py-3 pr-4"><StatusBadge status={r.status} /></td>
                    <td className="py-3 pr-4" style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{r.Department?.name}</td>
                    <td className="py-3 pr-4" style={{ fontSize: 12, color: '#475569' }}>
                      {r.user?.firstName} {r.user?.lastName}
                    </td>
                    <td className="py-3" style={{ fontSize: 12, color: '#94a3b8' }}>
                      {r.updatedAt
                        ? new Date(r.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                  </tr>
                ))}
                {(data?.recentRisks ?? []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400 text-sm">No recent activity</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
